import { useEffect, useState, useCallback } from "react";
import { Globe } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/common/DataTable";

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4" data-testid={testid}>
      <div className="text-2xl font-semibold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
      {hint && <div className="text-[11px] text-muted-foreground/70 mt-0.5">{hint}</div>}
    </div>
  );
}

const Bar = ({ pct }) => (
  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
    <div className="h-full bg-teal-500 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get("/v1/i18n/admin/language-analytics")).data.data); } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-muted-foreground py-10 text-center">Loading analytics…</div>;
  const d = data;

  return (
    <div data-testid="analytics-tab" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Enabled languages" value={`${d.languages.enabled}/${d.languages.total}`} testid="an-languages" />
        <StatCard label="Locked business terms" value={d.dictionary.locked} hint={`${d.dictionary.approved} approved · ${d.dictionary.pending} pending`} testid="an-dictionary" />
        <StatCard label="Cached AI translations" value={d.translationCache.total} testid="an-translations" />
        <StatCard label="Active users (with pref)" value={Object.values(d.adoption).reduce((a, b) => a + b, 0)} testid="an-adoption" />
      </div>

      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">UI pack coverage</h3>
        <div className="space-y-3" data-testid="coverage-list">
          {d.coverage.map((c) => (
            <div key={c.code} className="grid grid-cols-[160px_1fr_auto] items-center gap-3" data-testid={`coverage-${c.code}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-foreground truncate">{c.nativeName || c.name}</span>
                <span className="text-[11px] text-muted-foreground uppercase">{c.code}</span>
                {c.direction === "rtl" && <Badge color="violet">RTL</Badge>}
              </div>
              <Bar pct={c.coveragePct} />
              <div className="text-xs text-muted-foreground tabular-nums w-28 text-right">
                {c.entries} keys · {c.users} users
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
